import { AuthTokens } from './auth';

// Scoped to the FES host, because teams and roles are per-stage: a cons sync record must
// never satisfy a dev lookup. Versioned so clients discard records created before email was
// returned by the local proxy.
function syncKey(baseUrl: string): string {
  return `agent_backoffice_auth_sync_v3_${baseUrl}`;
}

export type Pagination = {
  total?: number;
  limit?: number;
  offset?: number;
};

export type Team = {
  teamId: string;
  name: string;
  description?: string;
  status: TeamStatus | string;
  installationId?: number;
  allowedRepos?: string[];
  jiraProjects?: JiraProject[];
  jiraConnection?: JiraConnection;
  triggerTokens?: TriggerToken[];
  githubPrTriggers?: GitHubPrTrigger[];
  azureDevOps?: AzureDevOpsSettings;
  metadata?: {
    createdAt?: string;
    createdBy?: string;
    updatedAt?: string;
    updatedBy?: string;
  };
};

export type JiraConnection = {
  mode?: 'default' | 'dedicated' | 'disabled';
  status: 'active' | 'disabled';
  baseUrl?: string;
};

// The API never returns the callback URL itself, only this masked copy.
export type AzureDevOpsSettings = {
  statusCallbackUrlMasked?: string;
};

export type TeamListResponse = {
  data?: Team[];
  pagination?: Pagination;
};

export type TeamResponse = {
  data?: Team;
};

export type TeamStatus = 'active' | 'inactive' | 'suspended';

export type JiraProject = {
  projectKey: string;
  agentId: string;
  description?: string;
  status: 'active' | 'revoked' | string;
  createdAt: string;
  createdBy?: string;
  revokedAt?: string;
};

export type WebhookKeyListResponse = {
  data?: JiraProject[];
};

export type WebhookKeyResponse = {
  data?: JiraProject;
};

export type CreateWebhookKeyRequest = {
  projectKey: string;
  agentId: string;
  description?: string;
};

export type WebhookKeyCreatedResponse = {
  data?: {
    token: string;
    projectKey: string;
    agentId: string;
    issuedAt?: string;
  };
};

export type TriggerTokenChannel = 'jira' | 'azure-devops' | 'pipeline';

export type TriggerToken = {
  tokenId: string;
  channel: TriggerTokenChannel;
  status: 'active' | 'revoked' | string;
  projectKey?: string;
  defaultAgentId?: string;
  allowedAgentIds?: string[];
  description?: string;
  createdAt: string;
  createdBy?: string;
  revokedAt?: string;
};

export type TriggerTokenListResponse = {
  data?: TriggerToken[];
};

export type TriggerTokenResponse = {
  data?: TriggerToken;
};

export type CreateTriggerTokenRequest = {
  channel: TriggerTokenChannel;
  projectKey?: string;
  defaultAgentId?: string;
  allowedAgentIds?: string[];
  description?: string;
};

export type TriggerTokenCreatedResponse = {
  data?: {
    token: string;
    tokenId: string;
    channel: TriggerTokenChannel;
    projectKey?: string;
    defaultAgentId?: string;
    allowedAgentIds?: string[];
    description?: string;
    issuedAt?: string;
  };
};

export type GitHubPrTrigger = {
  triggerId: string;
  triggerWord: string;
  normalizedTriggerWord: string;
  agentId: string;
  enabled: boolean;
  createdAt: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
};

export type GitHubPrTriggerListResponse = {
  data?: GitHubPrTrigger[];
};

export type GitHubPrTriggerResponse = {
  data?: GitHubPrTrigger;
};

export type CreateGitHubPrTriggerRequest = {
  triggerWord: string;
  agentId: string;
  enabled?: boolean;
};

export type UpdateGitHubPrTriggerRequest = Partial<CreateGitHubPrTriggerRequest>;

export type UpdateTeamRequest = {
  name?: string;
  description?: string;
  status?: TeamStatus;
  jiraConnection?: JiraConnection;
};

export type CreateTeamRequest = {
  name: string;
  description?: string;
  jiraConnection?: JiraConnection;
};

export type InstallTeamRequest = {
  installationId: number;
};

export type Agent = {
  agentId: string;
  name: string;
  description?: string;
  teamId?: string;
  isGlobal?: boolean;
} & Record<string, unknown>;

export type AgentListResponse = {
  data?: Agent[];
  pagination?: Pagination;
};

export type AgentResponse = {
  data?: Agent;
};

export type UpdateAgentRequest = {
  [key: string]: unknown;
};

export type UpdateTemplateRequest = {
  [key: string]: unknown;
};

export type CreateAgentRequest = {
  name: string;
  description?: string;
  isGlobal?: boolean;
  llmConfig?: Record<string, unknown>;
  systemInstructions?: Array<Record<string, unknown>>;
  codexAssets?: Array<Record<string, unknown>>;
  attachedSkillIds?: string[];
};

export type AgentTemplate = {
  templateId: string;
  name: string;
  description?: string;
} & Record<string, unknown>;

export type AgentTemplateListResponse = {
  data?: AgentTemplate[];
  pagination?: Pagination;
};

export type AgentTemplateResponse = {
  data?: AgentTemplate;
};

export type CreateTemplateRequest = {
  name: string;
  description?: string;
  llmConfig?: Record<string, unknown>;
  systemInstructions?: Array<Record<string, unknown>>;
  codexAssets?: Array<Record<string, unknown>>;
  attachedSkillIds?: string[];
};

export type SkillOwner = 'git' | 'ui';

export type Skill = {
  skillId: string;
  name: string;
  description?: string;
  managedBy?: SkillOwner;
  teamId?: string;
} & Record<string, unknown>;

export function isRepositoryManagedSkill(skill: Pick<Skill, 'managedBy'>): boolean {
  return skill.managedBy === 'git';
}

export function isSharedSkill(skill: Pick<Skill, 'teamId'>): boolean {
  return !skill.teamId;
}

export type SkillListResponse = {
  data?: Skill[];
  pagination?: Pagination;
};

export type SkillResponse = {
  data?: Skill;
};

export type UpdateSkillRequest = {
  [key: string]: unknown;
};

export type CreateSkillRequest = {
  name: string;
  description?: string;
  files: Array<Record<string, unknown>>;
};

export type Environment = {
  environmentId: string;
  name: string;
  versions: string[];
};

export type EnvironmentListResponse = {
  data?: Environment[];
};

export type ReasoningEffort = 'minimal' | 'low' | 'medium' | 'high' | 'xhigh';
export type ModelStatus = 'active' | 'inactive';

export type ModelDefinition = {
  modelId: string;
  provider: string;
  model: string;
  displayName: string;
  supportedReasoningEfforts: ReasoningEffort[];
  defaultReasoningEffort: ReasoningEffort;
  contextWindowTokens?: number;
  autoCompactTokenLimit?: number;
  status: ModelStatus;
  createdAt: string;
  updatedAt: string;
};

export type ModelListResponse = { data?: ModelDefinition[] };
export type ModelResponse = { data?: ModelDefinition };
export type CreateModelRequest = Omit<ModelDefinition, 'modelId' | 'createdAt' | 'updatedAt'>;
export type UpdateModelRequest = Partial<Omit<CreateModelRequest, 'contextWindowTokens' | 'autoCompactTokenLimit'>> & {
  contextWindowTokens?: number | null;
  autoCompactTokenLimit?: number | null;
};

export type GitHubRepository = {
  id: number;
  name: string;
  full_name: string;
  private: boolean;
  node_id?: string;
};

export type UnclaimedInstallation = {
  installationId: number;
  accountLogin: string;
  accountId: number;
  accountType?: 'User' | 'Organization' | string;
  repositories?: GitHubRepository[];
  createdAt?: string;
};

export type UnclaimedInstallationListResponse = {
  data?: UnclaimedInstallation[];
  pagination?: Pagination;
};

export type JobStatus = 'QUEUED' | 'CANCELLED' | 'STARTING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'PURGED';

export type JobUsage = {
  inputTokens?: number;
  outputTokens?: number;
  cachedInputTokens?: number;
  reasoningOutputTokens?: number;
};

export type Job = {
  requestId: string;
  agentId?: string;
  status?: JobStatus | string;
  issueId?: string;
  initialPrompt?: string;
  email?: string;
  createdAt?: string;
  updatedAt?: string;
  installationId?: number;
  teamId?: string;
  userId?: string;
  repositoryName?: string;
  githubOrg?: string;
  folderId?: string;
  usage?: JobUsage;
  model?: string;
  costUsd?: number;
  origin?: JobOrigin;
  azureResource?: { id?: number };
  dispatchMetadata?: Record<string, unknown>;
  context?: { repositories?: JobContextRepository[] };
  results?: JobWorkResult[];
};

export type JobContextRepository = {
  name?: string;
  githubOrg?: string;
  branchName?: string;
  targetBranch?: string;
  azureProject?: string;
  azureRepositoryUrl?: string;
};

/** A branch the agent pushed, reported by the skill's push script. */
export type JobWorkResult = {
  kind?: string;
  provider?: string;
  organization?: string;
  project?: string;
  repository?: string;
  branch?: string;
  commitSha?: string;
  webUrl?: string;
};

export type JobCallbackAttempt = {
  attempt: number;
  httpStatus?: number;
  body?: string;
  error?: string;
  durationMs: number;
};

export type JobTaskEvent = {
  timestamp: string;
  eventId?: string;
  kind?: 'status' | 'callback';
  lastStatus?: string;
  errorMessage?: string;
  callback?: JobCallbackAttempt;
};

export type JobReport = {
  requestId: string;
  status?: string;
  usage?: JobUsage;
  model?: string;
  costUsd?: number;
  taskEvents?: JobTaskEvent[];
  agentProtocol?: {
    model?: string;
    createdAt?: string;
    endedAt?: string;
    rawPayload?: string;
  };
};

export type JobOrigin = {
  channel?: JobChannel | string;
  principalType?: 'user' | 'service' | 'trigger-token' | string;
  principalId?: string;
  teamId?: string;
  userId?: string;
  scheduleId?: string;
};

export type JobChannel =
  | 'chat'
  | 'github-pr'
  | 'jira'
  | 'azure-devops'
  | 'pipeline'
  | 'api'
  | 'cli'
  | 'schedule';

export type JobListResponse = {
  data?: Job[];
  pagination?: Pagination;
};

export type JobFilters = {
  search?: string;
  status?: string;
  teamId?: string;
  agentId?: string;
  channel?: string;
  from?: string;
};

export type UsageTotals = {
  jobs: number;
  pricedJobs: number;
  unpricedJobs: number;
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
  reasoningOutputTokens: number;
  totalTokens: number;
  costUsd: number;
};

export type UsageBreakdown = UsageTotals & {
  model?: string;
  teamId?: string;
  userId?: string;
  agentId?: string;
  agentName?: string;
  repository?: string;
  category?: string;
  skill?: string;
  reasoningEffort?: string;
  date?: string;
};

export type UsageFacet = {
  value: string;
  label?: string;
};

export type UsageReportFilters = {
  teamId?: string;
  userId?: string;
  agentId?: string;
  repository?: string;
  category?: string;
  skill?: string;
  reasoningEffort?: string;
  model?: string;
};

export type UsageReport = {
  currency: 'USD';
  from: string;
  to: string;
  filters: UsageReportFilters;
  facets: {
    teams: UsageFacet[];
    users: UsageFacet[];
    agents: UsageFacet[];
    repositories: UsageFacet[];
    categories: UsageFacet[];
    skills: UsageFacet[];
    efforts: UsageFacet[];
    models: UsageFacet[];
  };
  summary: UsageTotals;
  byModel: UsageBreakdown[];
  byTeam: UsageBreakdown[];
  byUser: UsageBreakdown[];
  byAgent: UsageBreakdown[];
  byRepository: UsageBreakdown[];
  byCategory: UsageBreakdown[];
  bySkill: UsageBreakdown[];
  byEffort: UsageBreakdown[];
  byDay: UsageBreakdown[];
};

export type StartJobRequest = {
  requestId: string;
  agentId: string;
};

export type StartJobResponse = {
  message?: string;
};

export type JobStatusResponse = {
  requestId: string;
  status: JobStatus | string;
  folderId?: string;
};

export type ChatMessageItem = {
  id: string;
  role: string;
  createdAt: string;
  content?: string;
  payload?: Record<string, unknown>;
};

export type ChatMessagesResponse = {
  items: ChatMessageItem[];
  cursor: {
    latestAgentStreamId: string;
    hasMore: boolean;
  };
};

export type SendChatMessageResponse = {
  messageId: string;
  status: string;
};

export type ToolKind = 'command_execution' | 'mcp_tool_call' | 'file_change' | 'web_search' | 'todo_list';

export type ChatSseEvent = {
  id: string;
  type:
    | 'text'
    | 'status'
    | 'error'
    | 'done'
    | 'turn_complete'
    | 'text_delta'
    | 'reasoning_delta'
    | 'tool_call_start'
    | 'tool_call_delta'
    | 'tool_call_end'
    | 'message_end'
    | 'telemetry';
  content?: string;
  message?: string;
  reason?: string;
  progress?: number;
  turnNumber?: number;
  exitCode?: number;
  duration?: number;
  itemId?: string;
  delta?: string;
  tool?: ToolKind;
  preview?: string;
  status?: 'ok' | 'error';
  source?: 'codex_stderr' | 'codex_stdout_unparsed' | string;
};

export type FesConfig = {
  baseUrl: string;
};

function assertFesConfig(config: FesConfig): void {
  if (!config.baseUrl) {
    throw new Error('Missing FES config: REACT_APP_FES_BASE_URL');
  }
}

export class FesError extends Error {
  public readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'FesError';
    this.status = status;
  }
}

export function isForbidden(error: unknown): boolean {
  return error instanceof FesError && error.status === 403;
}

async function fetchJson(config: FesConfig, path: string, tokens: AuthTokens, init?: RequestInit): Promise<unknown> {
  assertFesConfig(config);

  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${tokens.accessToken}`);

  const response = await window.fetch(new URL(path, config.baseUrl).toString(), {
    ...init,
    headers,
  });

  if (!response.ok) {
    const errorBody = await response.text();
    let errorMessage = errorBody;

    try {
      const parsedError = JSON.parse(errorBody);
      errorMessage = parsedError?.error?.message || parsedError?.message || errorBody;
    } catch {
      errorMessage = errorBody;
    }

    throw new FesError(errorMessage || `FES request failed (${response.status}) for ${path}`, response.status);
  }

  const body = await response.text();

  if (response.status === 204 || !body) {
    return null;
  }

  return JSON.parse(body);
}

export type SyncedUser = {
  userId: string;
  roles: string[];
  teamIds: string[];
  email?: string;
};

function readCachedSync(config: FesConfig, accessToken: string): SyncedUser | null {
  const raw = window.sessionStorage.getItem(syncKey(config.baseUrl));

  if (!raw) {
    return null;
  }

  try {
    const cached = JSON.parse(raw) as { accessToken: string; user: SyncedUser };
    return cached.accessToken === accessToken ? cached.user : null;
  } catch {
    return null;
  }
}

export async function syncAuthenticatedUser(config: FesConfig, tokens: AuthTokens): Promise<SyncedUser> {
  const cached = readCachedSync(config, tokens.accessToken);

  if (cached) {
    return cached;
  }

  const response = (await fetchJson(config, '/auth/sync', tokens, {
    method: 'POST',
    headers: tokens.idToken ? { 'x-id-token': tokens.idToken } : undefined,
  })) as { data?: Partial<SyncedUser> } | null;

  const data = response?.data ?? {};
  const user: SyncedUser = {
    userId: data.userId ?? '',
    roles: data.roles ?? [],
    teamIds: data.teamIds ?? [],
    ...(data.email && { email: data.email }),
  };

  window.sessionStorage.setItem(syncKey(config.baseUrl), JSON.stringify({ accessToken: tokens.accessToken, user }));

  return user;
}

export async function getTeams(
  config: FesConfig,
  tokens: AuthTokens,
  params: { limit?: number; offset?: number } = {}
): Promise<TeamListResponse> {
  const query = new URLSearchParams();
  if (params.limit !== undefined) query.set('limit', String(params.limit));
  if (params.offset !== undefined) query.set('offset', String(params.offset));
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return fetchJson(config, `/config-service/teams${suffix}`, tokens) as Promise<TeamListResponse>;
}

export async function getAllTeams(config: FesConfig, tokens: AuthTokens): Promise<Team[]> {
  const teams: Team[] = [];
  const limit = 100;
  let offset = 0;
  let total = 0;
  do {
    const response = await getTeams(config, tokens, { limit, offset });
    const page = response.data || [];
    teams.push(...page);
    total = response.pagination?.total ?? teams.length;
    offset += page.length;
    if (page.length === 0) break;
  } while (offset < total);
  return teams;
}

export async function getTeam(config: FesConfig, tokens: AuthTokens, teamId: string): Promise<TeamResponse> {
  return fetchJson(config, `/config-service/teams/${teamId}`, tokens) as Promise<TeamResponse>;
}

export async function createTeam(config: FesConfig, tokens: AuthTokens, team: CreateTeamRequest): Promise<TeamResponse> {
  return fetchJson(config, '/config-service/teams', tokens, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(team),
  }) as Promise<TeamResponse>;
}

export async function getWebhookKeys(config: FesConfig, tokens: AuthTokens, teamId: string): Promise<WebhookKeyListResponse> {
  return fetchJson(config, `/config-service/teams/${teamId}/webhook-keys`, tokens) as Promise<WebhookKeyListResponse>;
}

export async function createWebhookKey(
  config: FesConfig,
  tokens: AuthTokens,
  teamId: string,
  webhookKey: CreateWebhookKeyRequest
): Promise<WebhookKeyCreatedResponse> {
  return fetchJson(config, `/config-service/teams/${teamId}/webhook-keys`, tokens, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(webhookKey),
  }) as Promise<WebhookKeyCreatedResponse>;
}

export async function deleteWebhookKey(
  config: FesConfig,
  tokens: AuthTokens,
  teamId: string,
  projectKey: string
): Promise<WebhookKeyResponse> {
  return fetchJson(config, `/config-service/teams/${teamId}/webhook-keys/${encodeURIComponent(projectKey)}`, tokens, {
    method: 'DELETE',
  }) as Promise<WebhookKeyResponse>;
}

export async function getTriggerTokens(
  config: FesConfig,
  tokens: AuthTokens,
  teamId: string
): Promise<TriggerTokenListResponse> {
  return fetchJson(config, `/config-service/v2/teams/${teamId}/trigger-tokens`, tokens) as Promise<TriggerTokenListResponse>;
}

export async function createTriggerToken(
  config: FesConfig,
  tokens: AuthTokens,
  teamId: string,
  triggerToken: CreateTriggerTokenRequest
): Promise<TriggerTokenCreatedResponse> {
  return fetchJson(config, `/config-service/v2/teams/${teamId}/trigger-tokens`, tokens, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(triggerToken),
  }) as Promise<TriggerTokenCreatedResponse>;
}

export async function deleteTriggerToken(
  config: FesConfig,
  tokens: AuthTokens,
  teamId: string,
  tokenId: string
): Promise<TriggerTokenResponse> {
  return fetchJson(
    config,
    `/config-service/v2/teams/${teamId}/trigger-tokens/${encodeURIComponent(tokenId)}`,
    tokens,
    { method: 'DELETE' }
  ) as Promise<TriggerTokenResponse>;
}

export async function getGitHubPrTriggers(
  config: FesConfig,
  tokens: AuthTokens,
  teamId: string
): Promise<GitHubPrTriggerListResponse> {
  return fetchJson(
    config,
    `/config-service/teams/${teamId}/github-pr-triggers`,
    tokens
  ) as Promise<GitHubPrTriggerListResponse>;
}

export async function createGitHubPrTrigger(
  config: FesConfig,
  tokens: AuthTokens,
  teamId: string,
  trigger: CreateGitHubPrTriggerRequest
): Promise<GitHubPrTriggerResponse> {
  return fetchJson(config, `/config-service/teams/${teamId}/github-pr-triggers`, tokens, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(trigger),
  }) as Promise<GitHubPrTriggerResponse>;
}

export async function updateGitHubPrTrigger(
  config: FesConfig,
  tokens: AuthTokens,
  teamId: string,
  triggerId: string,
  trigger: UpdateGitHubPrTriggerRequest
): Promise<GitHubPrTriggerResponse> {
  return fetchJson(
    config,
    `/config-service/teams/${teamId}/github-pr-triggers/${encodeURIComponent(triggerId)}`,
    tokens,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(trigger),
    }
  ) as Promise<GitHubPrTriggerResponse>;
}

export async function deleteGitHubPrTrigger(
  config: FesConfig,
  tokens: AuthTokens,
  teamId: string,
  triggerId: string
): Promise<void> {
  await fetchJson(
    config,
    `/config-service/teams/${teamId}/github-pr-triggers/${encodeURIComponent(triggerId)}`,
    tokens,
    { method: 'DELETE' }
  );
}

export async function updateTeam(
  config: FesConfig,
  tokens: AuthTokens,
  teamId: string,
  team: UpdateTeamRequest
): Promise<TeamResponse> {
  return fetchJson(config, `/config-service/teams/${teamId}`, tokens, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(team),
  }) as Promise<TeamResponse>;
}

export async function setTeamAzureDevOps(
  config: FesConfig,
  tokens: AuthTokens,
  teamId: string,
  settings: { statusCallbackUrl: string }
): Promise<TeamResponse> {
  return fetchJson(config, `/config-service/teams/${teamId}/azure-devops`, tokens, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(settings),
  }) as Promise<TeamResponse>;
}

export async function installTeam(
  config: FesConfig,
  tokens: AuthTokens,
  teamId: string,
  installRequest: InstallTeamRequest
): Promise<TeamResponse> {
  return fetchJson(config, `/config-service/teams/${teamId}/install`, tokens, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(installRequest),
  }) as Promise<TeamResponse>;
}

export type User = {
  userId: string;
  email?: string;
  roles?: string[];
  teamIds?: string[];
  metadata?: {
    createdAt?: string;
    updatedAt?: string;
  };
};

export type UserListResponse = {
  data?: User[];
};

export type UserResponse = {
  data?: User;
};

export async function getUsers(config: FesConfig, tokens: AuthTokens): Promise<UserListResponse> {
  return fetchJson(config, '/config-service/users', tokens) as Promise<UserListResponse>;
}

export async function updateUserTeams(
  config: FesConfig,
  tokens: AuthTokens,
  userId: string,
  teamIds: string[]
): Promise<UserResponse> {
  return fetchJson(config, `/config-service/users/${encodeURIComponent(userId)}/teams`, tokens, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ teamIds }),
  }) as Promise<UserResponse>;
}

export async function getAgents(config: FesConfig, tokens: AuthTokens): Promise<AgentListResponse> {
  const agents: Agent[] = [];
  const limit = 100;
  let offset = 0;
  let total = 0;
  do {
    const response = (await fetchJson(
      config,
      `/config-service/agents?limit=${limit}&offset=${offset}`,
      tokens
    )) as AgentListResponse;
    const page = response.data || [];
    agents.push(...page);
    total = response.pagination?.total ?? agents.length;
    offset += page.length;
    if (page.length === 0) break;
  } while (offset < total);

  return { data: agents, pagination: { total: agents.length, limit, offset: 0 } };
}

export async function getAllAgents(config: FesConfig, tokens: AuthTokens): Promise<Agent[]> {
  return (await getAgents(config, tokens)).data || [];
}

export async function getAgent(config: FesConfig, tokens: AuthTokens, agentId: string): Promise<AgentResponse> {
  return fetchJson(config, `/config-service/agents/${agentId}`, tokens) as Promise<AgentResponse>;
}

export async function createAgent(
  config: FesConfig,
  tokens: AuthTokens,
  agent: CreateAgentRequest,
  options: { teamId?: string } = {}
): Promise<AgentResponse> {
  return fetchJson(config, '/config-service/agents', tokens, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(options.teamId ? { 'x-active-team-id': options.teamId } : {}),
    },
    body: JSON.stringify(agent),
  }) as Promise<AgentResponse>;
}

export async function updateAgent(
  config: FesConfig,
  tokens: AuthTokens,
  agentId: string,
  agent: UpdateAgentRequest
): Promise<AgentResponse> {
  return fetchJson(config, `/config-service/agents/${agentId}`, tokens, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(agent),
  }) as Promise<AgentResponse>;
}

export async function deleteAgent(config: FesConfig, tokens: AuthTokens, agentId: string): Promise<void> {
  await fetchJson(config, `/config-service/agents/${agentId}`, tokens, {
    method: 'DELETE',
  });
}

export async function getTemplates(config: FesConfig, tokens: AuthTokens): Promise<AgentTemplateListResponse> {
  return fetchJson(config, '/config-service/templates', tokens) as Promise<AgentTemplateListResponse>;
}

export async function getTemplate(config: FesConfig, tokens: AuthTokens, templateId: string): Promise<AgentTemplateResponse> {
  return fetchJson(config, `/config-service/templates/${templateId}`, tokens) as Promise<AgentTemplateResponse>;
}

export async function createTemplate(
  config: FesConfig,
  tokens: AuthTokens,
  template: CreateTemplateRequest
): Promise<AgentTemplateResponse> {
  return fetchJson(config, '/config-service/templates', tokens, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(template),
  }) as Promise<AgentTemplateResponse>;
}

export async function updateTemplate(
  config: FesConfig,
  tokens: AuthTokens,
  templateId: string,
  template: UpdateTemplateRequest
): Promise<AgentTemplateResponse> {
  return fetchJson(config, `/config-service/templates/${templateId}`, tokens, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(template),
  }) as Promise<AgentTemplateResponse>;
}

export async function deleteTemplate(config: FesConfig, tokens: AuthTokens, templateId: string): Promise<void> {
  await fetchJson(config, `/config-service/templates/${templateId}`, tokens, {
    method: 'DELETE',
  });
}

export async function getSkills(config: FesConfig, tokens: AuthTokens): Promise<SkillListResponse> {
  return fetchJson(config, '/config-service/skills', tokens) as Promise<SkillListResponse>;
}

export async function getSkill(config: FesConfig, tokens: AuthTokens, skillId: string): Promise<SkillResponse> {
  return fetchJson(config, `/config-service/skills/${skillId}`, tokens) as Promise<SkillResponse>;
}

export async function createSkill(
  config: FesConfig,
  tokens: AuthTokens,
  skill: CreateSkillRequest,
  options: { teamId?: string } = {}
): Promise<SkillResponse> {
  return fetchJson(config, '/config-service/skills', tokens, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(options.teamId ? { 'x-active-team-id': options.teamId } : {}),
    },
    body: JSON.stringify(skill),
  }) as Promise<SkillResponse>;
}

export async function updateSkill(
  config: FesConfig,
  tokens: AuthTokens,
  skillId: string,
  skill: UpdateSkillRequest
): Promise<SkillResponse> {
  return fetchJson(config, `/config-service/skills/${skillId}`, tokens, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(skill),
  }) as Promise<SkillResponse>;
}

export async function deleteSkill(config: FesConfig, tokens: AuthTokens, skillId: string): Promise<void> {
  await fetchJson(config, `/config-service/skills/${skillId}`, tokens, {
    method: 'DELETE',
  });
}

export async function getEnvironments(config: FesConfig, tokens: AuthTokens): Promise<EnvironmentListResponse> {
  return fetchJson(config, '/config-service/environments', tokens) as Promise<EnvironmentListResponse>;
}

export async function getModels(
  config: FesConfig,
  tokens: AuthTokens,
  status?: ModelStatus
): Promise<ModelListResponse> {
  const suffix = status ? `?status=${encodeURIComponent(status)}` : '';
  return fetchJson(config, `/config-service/models${suffix}`, tokens) as Promise<ModelListResponse>;
}

export async function createModel(
  config: FesConfig,
  tokens: AuthTokens,
  model: CreateModelRequest
): Promise<ModelResponse> {
  return fetchJson(config, '/config-service/models', tokens, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(model),
  }) as Promise<ModelResponse>;
}

export async function updateModel(
  config: FesConfig,
  tokens: AuthTokens,
  modelId: string,
  model: UpdateModelRequest
): Promise<ModelResponse> {
  return fetchJson(config, `/config-service/models/${encodeURIComponent(modelId)}`, tokens, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(model),
  }) as Promise<ModelResponse>;
}

export async function getUnclaimedInstallations(
  config: FesConfig,
  tokens: AuthTokens
): Promise<UnclaimedInstallationListResponse> {
  return fetchJson(config, '/config-service/unclaimed-installations', tokens) as Promise<UnclaimedInstallationListResponse>;
}

export const JOBS_PAGE_SIZE = 20;

export async function getJobs(
  config: FesConfig,
  tokens: AuthTokens,
  params: JobFilters & { limit?: number; offset?: number } = {}
): Promise<JobListResponse> {
  const query = new URLSearchParams({
    limit: String(params.limit ?? JOBS_PAGE_SIZE),
    offset: String(params.offset ?? 0),
  });
  const search = params.search?.trim();
  if (search) query.set('search', search);
  if (params.status) query.set('status', params.status);
  if (params.teamId) query.set('teamId', params.teamId);
  if (params.agentId) query.set('agentId', params.agentId);
  if (params.channel) query.set('channel', params.channel);
  if (params.from) query.set('from', params.from);

  return fetchJson(config, `/job-service/v2/jobs?${query.toString()}`, tokens) as Promise<JobListResponse>;
}

export type ScheduleTrigger = {
  cron: string;
  timezone?: string;
};

export type ScheduleJobTemplate = {
  agentId: string;
  prompt?: string;
  context?: {
    repositories?: Array<{ name: string; githubOrg?: string; branchName?: string; installationId?: number }>;
    issues?: Array<{ issueId: string; context?: string }>;
    folders?: string[];
    skills?: string[];
  };
  notify?: { email?: string };
  metadata?: Record<string, unknown>;
};

export type ScheduleLastRunStatus = 'DISPATCHED' | 'SUCCEEDED' | 'SKIPPED' | 'FAILED';

export type Schedule = {
  scheduleId: string;
  teamId?: string;
  createdBy?: string;
  name: string;
  description?: string;
  enabled?: boolean;
  trigger?: ScheduleTrigger;
  jobTemplate?: ScheduleJobTemplate;
  concurrencyPolicy?: 'skip' | 'allow';
  nextRunAt?: string;
  lastRunAt?: string;
  lastRunStatus?: ScheduleLastRunStatus | string;
  lastRequestId?: string;
  lastError?: string;
  consecutiveFailures?: number;
  runCount?: number;
  createdAt?: string;
  updatedAt?: string;
};

export type ScheduleListResponse = {
  data?: Schedule[];
  pagination?: Pagination;
};

export type ScheduleRunResult = {
  scheduleId?: string;
  status?: ScheduleLastRunStatus | string;
  requestId?: string;
  message?: string;
};

export type CreateScheduleRequest = {
  name: string;
  description?: string;
  enabled?: boolean;
  trigger: ScheduleTrigger;
  jobTemplate: ScheduleJobTemplate;
  concurrencyPolicy?: 'skip' | 'allow';
};

export type UpdateScheduleRequest = Partial<CreateScheduleRequest>;

export const SCHEDULES_PAGE_SIZE = 20;

export async function getSchedules(
  config: FesConfig,
  tokens: AuthTokens,
  params: { limit?: number; offset?: number } = {}
): Promise<ScheduleListResponse> {
  const query = new URLSearchParams({
    limit: String(params.limit ?? SCHEDULES_PAGE_SIZE),
    offset: String(params.offset ?? 0),
  });

  return fetchJson(config, `/job-service/v2/schedules?${query.toString()}`, tokens) as Promise<ScheduleListResponse>;
}

export async function createSchedule(
  config: FesConfig,
  tokens: AuthTokens,
  teamId: string,
  schedule: CreateScheduleRequest
): Promise<Schedule> {
  return fetchJson(config, '/job-service/v2/schedules', tokens, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-active-team-id': teamId,
    },
    body: JSON.stringify(schedule),
  }) as Promise<Schedule>;
}

export async function updateSchedule(
  config: FesConfig,
  tokens: AuthTokens,
  scheduleId: string,
  schedule: UpdateScheduleRequest
): Promise<Schedule> {
  return fetchJson(config, `/job-service/v2/schedules/${scheduleId}`, tokens, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(schedule),
  }) as Promise<Schedule>;
}

export async function deleteSchedule(config: FesConfig, tokens: AuthTokens, scheduleId: string): Promise<void> {
  await fetchJson(config, `/job-service/v2/schedules/${scheduleId}`, tokens, { method: 'DELETE' });
}

export async function runScheduleNow(
  config: FesConfig,
  tokens: AuthTokens,
  scheduleId: string
): Promise<ScheduleRunResult> {
  return fetchJson(config, `/job-service/v2/schedules/${scheduleId}/run`, tokens, {
    method: 'POST',
  }) as Promise<ScheduleRunResult>;
}

export async function getScheduleRuns(
  config: FesConfig,
  tokens: AuthTokens,
  scheduleId: string,
  params: { limit?: number; offset?: number } = {}
): Promise<JobListResponse> {
  const query = new URLSearchParams({
    limit: String(params.limit ?? 10),
    offset: String(params.offset ?? 0),
  });

  return fetchJson(
    config,
    `/job-service/v2/schedules/${scheduleId}/runs?${query.toString()}`,
    tokens
  ) as Promise<JobListResponse>;
}

export async function getUsageReport(
  config: FesConfig,
  tokens: AuthTokens,
  params: { from: string; to: string } & UsageReportFilters
): Promise<UsageReport> {
  const query = new URLSearchParams({ from: params.from, to: params.to });
  const filterKeys: Array<keyof UsageReportFilters> = [
    'teamId',
    'userId',
    'agentId',
    'repository',
    'category',
    'skill',
    'reasoningEffort',
    'model',
  ];
  filterKeys.forEach(key => {
    if (params[key]) query.set(key, params[key]!);
  });
  return fetchJson(config, `/job-service/usage-report?${query.toString()}`, tokens) as Promise<UsageReport>;
}

export async function startJob(
  config: FesConfig,
  tokens: AuthTokens,
  request: StartJobRequest,
  options: { teamId?: string } = {}
): Promise<StartJobResponse> {
  return fetchJson(config, '/job-service/v2/jobs', tokens, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(options.teamId ? { 'x-active-team-id': options.teamId } : {}),
    },
    body: JSON.stringify({
      ...request,
      execution: {
        mode: 'immediate',
      },
    }),
  }) as Promise<StartJobResponse>;
}

export async function getJobStatus(config: FesConfig, tokens: AuthTokens, requestId: string): Promise<JobStatusResponse> {
  return fetchJson(config, `/job-service/v2/jobs/${encodeURIComponent(requestId)}/status`, tokens) as Promise<JobStatusResponse>;
}

export async function getJobReport(config: FesConfig, tokens: AuthTokens, requestId: string): Promise<JobReport> {
  return fetchJson(config, `/job-service/v2/jobs/${encodeURIComponent(requestId)}/report`, tokens) as Promise<JobReport>;
}

export async function getChatMessages(
  config: FesConfig,
  tokens: AuthTokens,
  requestId: string,
  limit = 500
): Promise<ChatMessagesResponse> {
  return fetchJson(
    config,
    `/chat-service/chats/${encodeURIComponent(requestId)}/messages?limit=${limit}`,
    tokens
  ) as Promise<ChatMessagesResponse>;
}

export async function sendChatMessage(
  config: FesConfig,
  tokens: AuthTokens,
  requestId: string,
  content: string
): Promise<SendChatMessageResponse> {
  return fetchJson(config, `/chat-service/chats/${encodeURIComponent(requestId)}/send`, tokens, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ content }),
  }) as Promise<SendChatMessageResponse>;
}

function parseChatSseFrame(rawFrame: string): { id: string; data: string } {
  let id = '';
  let data = '';

  rawFrame.split('\n').forEach(line => {
    const normalizedLine = line.endsWith('\r') ? line.slice(0, -1) : line;

    if (normalizedLine.startsWith('id: ')) {
      id = normalizedLine.slice(4);
    } else if (normalizedLine.startsWith('data: ')) {
      data = data ? `${data}\n${normalizedLine.slice(6)}` : normalizedLine.slice(6);
    }
  });

  return { id, data };
}

function parseChatSseFrames(text: string): Array<{ id: string; data: string }> {
  return text
    .split(/\n\n/)
    .map(parseChatSseFrame)
    .filter(frame => Boolean(frame.data));
}

function sleep(milliseconds: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, milliseconds));
}

export function connectChatSse(
  config: FesConfig,
  tokens: AuthTokens,
  requestId: string,
  after: string,
  onEvent: (event: ChatSseEvent) => void,
  onError: (error: Event) => void
): { close: () => void } {
  assertFesConfig(config);

  let stopped = false;
  let lastId = after;
  let abortController: AbortController | null = null;

  const run = async () => {
    while (!stopped) {
      abortController = new AbortController();

      try {
        const url = new URL(
          `/chat-service/chats/${encodeURIComponent(requestId)}/events`,
          config.baseUrl
        );
        url.searchParams.set('after', lastId);

        const response = await window.fetch(url.toString(), {
          headers: {
            Authorization: `Bearer ${tokens.accessToken}`,
            Accept: 'text/event-stream',
          },
          signal: abortController.signal,
        });

        if (stopped) {
          return;
        }

        if (response.status === 504) {
          await sleep(500);
          continue;
        }

        if (!response.ok) {
          onError(new Event('error'));
          return;
        }

        const responseBody = response.body as ReadableStream<Uint8Array> | null;

        if (!responseBody || typeof responseBody.getReader !== 'function') {
          const bodyText = await response.text();
          const frames = parseChatSseFrames(bodyText);
          let gotDone = false;

          for (const { id, data } of frames) {
            if (id) {
              lastId = id;
            }

            try {
              const event = { id, ...JSON.parse(data) } as ChatSseEvent;
              onEvent(event);

              if (event.type === 'done') {
                gotDone = true;
              }
            } catch {
              onEvent({ id, type: 'text', content: data });
            }
          }

          if (gotDone || stopped) {
            return;
          }

          await sleep(frames.length > 0 ? 200 : 1000);
          continue;
        }

        const reader = responseBody.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let gotDone = false;
        let frameCount = 0;

        while (!stopped) {
          const chunk = await reader.read();

          if (chunk.done) {
            break;
          }

          buffer += decoder.decode(chunk.value, { stream: true });

          let separatorIndex = buffer.indexOf('\n\n');
          while (separatorIndex >= 0) {
            const rawFrame = buffer.slice(0, separatorIndex);
            buffer = buffer.slice(separatorIndex + 2);

            if (rawFrame.trim()) {
              const { id, data } = parseChatSseFrame(rawFrame);

              if (id) {
                lastId = id;
              }

              if (data) {
                frameCount += 1;

                try {
                  const event = { id, ...JSON.parse(data) } as ChatSseEvent;
                  onEvent(event);

                  if (event.type === 'done') {
                    gotDone = true;
                  }
                } catch {
                  onEvent({ id, type: 'text', content: data });
                }
              }
            }

            separatorIndex = buffer.indexOf('\n\n');
          }

          if (gotDone) {
            break;
          }
        }

        try {
          await reader.cancel();
        } catch {
          // Ignore already-closed streams.
        }

        abortController = null;

        if (gotDone || stopped) {
          return;
        }

        await sleep(frameCount > 0 ? 200 : 1000);
      } catch (error) {
        if (stopped) {
          return;
        }

        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }

        await sleep(2000);
      }
    }
  };

  void run();

  return {
    close: () => {
      stopped = true;
      abortController?.abort();
    },
  };
}
